import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { io, type Socket } from "socket.io-client";
import { API_BASE, ApiError, apiRequest, getAccessToken, refreshSession } from "../../api/client";
import { startAzureRecognition, type AzureRecognitionHandle } from "../../hooks/azureSpeech";
import { SPEED_PRESETS, useLiveSettings } from "../../hooks/liveSettings";
import { UtteranceChunker } from "../../hooks/utteranceChunker";
import LiveSettingsCard from "./LiveSettingsCard";
import type { ConferenceSession, LoadedDictionary, SessionStatus } from "../../api/types";

interface QrResponse {
  joinUrl: string;
  sessionCode: string;
  qrDataUrl: string;
}

interface TranscriptLine {
  id: string;
  lang: string;
  text: string;
  isSource: boolean;
}

// Minimal ambient typing for the (non-standard, Chrome/Edge-only) Web Speech
// recognition API so this file compiles without pulling in a DOM-lib polyfill
// package for a browser-only, best-effort feature.
type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

function getSpeechRecognition(): (new () => SpeechRecognitionLike) | null {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export default function SessionControlPage() {
  const { sessionId = "" } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<ConferenceSession | null>(null);
  const [qr, setQr] = useState<QrResponse | null>(null);
  const [status, setStatus] = useState<SessionStatus | null>(null);
  const [listenerCounts, setListenerCounts] = useState<Record<string, number>>({});
  const [micLive, setMicLive] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  // Text the browser is *currently* hearing but hasn't finalized yet. Chrome
  // only fires a final result after it detects a pause in speech, which is
  // where the "the app is slow to pick up my voice" feeling actually comes
  // from — the mic itself is live the whole time. Showing this interim text
  // gives the operator instant proof the mic is capturing audio, even though
  // that text isn't sent to translation until it's finalized.
  const [interimText, setInterimText] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const azureRef = useRef<AzureRecognitionHandle | null>(null);
  const chunkerRef = useRef<UtteranceChunker | null>(null);
  // Every chunk sent for translation, oldest first, for "Download transcript" (the visible list keeps only the last 50).
  const transcriptLogRef = useRef<Array<{ at: number; text: string }>>([]);
  // Recent translation times reported by the server (see the pipeline:ok handler).
  const delaySamplesRef = useRef<number[]>([]);
  const [delay, setDelay] = useState<{ lastMs: number; avgMs: number; maxMs: number } | null>(null);
  // Ticks the chunker so a speaker's pause sends the words waiting (see UtteranceChunker.poll).
  const chunkPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(
    () => () => {
      if (chunkPollRef.current) clearInterval(chunkPollRef.current);
    },
    [],
  );

  // Operator-adjustable caption speed / live line. The running recognizer's
  // callbacks outlive any one render, so they read the CURRENT values via a ref.
  const [liveSettings, updateLiveSettings] = useLiveSettings();
  const liveSettingsRef = useRef(liveSettings);
  liveSettingsRef.current = liveSettings;
  const [serverLivePartials, setServerLivePartials] = useState(true);
  // The dictionaries this session translates with (loaded when it started), best priority first.
  const [loadedDictionaries, setLoadedDictionaries] = useState<LoadedDictionary[]>([]);
  const [engine, setEngine] = useState<"azure" | "browser" | null>(null);
  const micLiveRef = useRef(false);
  // The speech callback lives for as long as the mic is on, so it reads the
  // current status through a ref instead of a stale closure.
  const statusRef = useRef<SessionStatus | null>(null);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, qrRes] = await Promise.all([
          apiRequest<ConferenceSession>(`/sessions/${sessionId}`),
          // Tell the server which address we're using, so the QR code points at it
          // (and stays correct if this laptop joins a different network).
          apiRequest<QrResponse>(`/sessions/${sessionId}/qrcode`, { query: { baseUrl: window.location.origin } }),
        ]);
        if (cancelled) return;
        setSession(s);
        setStatus(s.status);
        setQr(qrRes);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : "Could not load this session.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Leaving this page must switch the microphone off. Without this, the
  // recognizer's auto-restart (see onend below) keeps the mic open in the
  // background after the operator navigates away.
  useEffect(
    () => () => {
      micLiveRef.current = false;
      recognitionRef.current?.stop();
      recognitionRef.current = null;
      void azureRef.current?.stop().catch(() => undefined);
      azureRef.current = null;
    },
    [],
  );

  useEffect(() => {
    // A function (not a fixed object) so every (re)connection sends the
    // CURRENT access token — the 15-minute token captured at page load would
    // otherwise be rejected the first time the socket reconnects after expiry.
    // API_BASE is "" (relative) unless the frontend is built for a separately-hosted
    // backend (see api/client.ts) — in which case this is an absolute cross-origin URL.
    const socket = io(`${API_BASE}/operator`, { auth: (cb) => cb({ token: getAccessToken() }) });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("session:join", { sessionId });
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => {
      setConnected(false);
      // The server refused the handshake (typically an expired token). Socket.IO
      // doesn't retry those by itself, so refresh the session and reconnect.
      if (!socket.active) {
        void refreshSession().then((ok) => {
          if (ok) socket.connect();
        });
      }
    });
    socket.on("session:joined", (payload: { active: boolean; run: { paused: boolean; dictionaries?: LoadedDictionary[] } | null; livePartials?: boolean }) => {
      if (payload.run) setStatus(payload.run.paused ? "PAUSED" : "LIVE");
      if (payload.run?.dictionaries) setLoadedDictionaries(payload.run.dictionaries);
      if (typeof payload.livePartials === "boolean") setServerLivePartials(payload.livePartials);
    });
    socket.on("session:state", (payload: { status: SessionStatus }) => setStatus(payload.status));
    socket.on("pipeline:error", (payload: { message: string }) => setActionError(payload.message));
    socket.on("pipeline:ok", (payload?: { latencyMs?: number; heldMs?: number }) => {
      setActionError(null);
      // How long the server took to translate this chunk (plus any wait to keep the order): the last
      // ten are averaged and shown next to the speed setting, so it can be tuned with numbers.
      if (typeof payload?.latencyMs === "number") {
        const total = payload.latencyMs + (payload.heldMs ?? 0);
        const samples = delaySamplesRef.current;
        samples.push(total);
        if (samples.length > 10) samples.shift();
        setDelay({
          lastMs: total,
          avgMs: samples.reduce((a, b) => a + b, 0) / samples.length,
          maxMs: Math.max(...samples),
        });
      }
    });

    const poll = setInterval(() => {
      if (session) {
        socket.emit("session:listenerCounts", { sessionId, targetLanguageCodes: session.targetLanguageCodes });
      }
    }, 4000);
    socket.on("session:listenerCounts", (payload: { counts: Record<string, number> }) => {
      setListenerCounts(payload.counts);
    });

    return () => {
      clearInterval(poll);
      socket.close();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, session?.targetLanguageCodes.join(",")]);

  const sendUtterance = useCallback(
    (text: string) => {
      // Nothing is sent while paused/stopped, or while the connection is down.
      // `volatile` means a sentence spoken during an outage is dropped instead
      // of being queued and replayed as a burst of stale captions on reconnect.
      const socket = socketRef.current;
      if (statusRef.current !== "LIVE" || !socket?.connected) return;
      socket.volatile.emit("utterance:new", { sessionId, text });
      transcriptLogRef.current.push({ at: Date.now(), text });
      if (transcriptLogRef.current.length > 5000) transcriptLogRef.current.shift();
      setTranscript((prev) => [
        ...prev.slice(-49),
        { id: `${Date.now()}-${Math.random()}`, lang: session?.sourceLanguageCode ?? "src", text, isSource: true },
      ]);
    },
    [sessionId, session?.sourceLanguageCode],
  );

  // Live provisional line: the words being spoken right now, translated and
  // shown to listeners before the phrase is finished. Not stored; replaced by
  // the permanent caption when the chunk is committed. Best-effort (volatile).
  const sendPartial = useCallback(
    (text: string) => {
      const socket = socketRef.current;
      if (statusRef.current !== "LIVE" || !socket?.connected) return;
      // With the live line switched off nothing is sent (saves Translator use);
      // an empty update is still allowed through so any line already on screen is cleared.
      if (text && !liveSettingsRef.current.livePreview) return;
      socket.volatile.emit("utterance:partial", { sessionId, text });
    },
    [sessionId],
  );

  // Apply a new speed to the running chunker at once; clear listeners' live line when it is switched off.
  useEffect(() => {
    chunkerRef.current?.setOptions(SPEED_PRESETS[liveSettings.speed].chunker);
  }, [liveSettings.speed]);
  useEffect(() => {
    if (!liveSettings.livePreview) sendPartial("");
  }, [liveSettings.livePreview, sendPartial]);

  async function callControl(action: "start" | "pause" | "resume" | "stop") {
    setActionError(null);
    const socket = socketRef.current;
    if (!socket?.connected) {
      setActionError("Not connected to the server. Check the network connection and try again.");
      return;
    }
    await new Promise<void>((resolve) => {
      socket
        .timeout(10_000)
        .emit(`session:${action}`, { sessionId }, (timeoutErr: Error | null, ack?: { ok: boolean; error?: string; run?: { dictionaries?: LoadedDictionary[] } }) => {
          if (timeoutErr || !ack) {
            setActionError(`The server did not answer the ${action} request. Please try again.`);
          } else if (!ack.ok) {
            // Leave the displayed status alone: the action did not happen.
            setActionError(ack.error ?? `Failed to ${action} session`);
          } else {
            if (action === "start" && ack.run?.dictionaries) setLoadedDictionaries(ack.run.dictionaries);
            if (action === "start" || action === "resume") setStatus("LIVE");
            if (action === "pause") setStatus("PAUSED");
            if (action === "stop") {
              setStatus("ENDED");
              stopMic();
            }
          }
          resolve();
        });
    });
  }

  /** Saves every chunk sent for translation, oldest first, as a UTF-8 text file (handy for tuning and review). */
  function downloadTranscript() {
    const log = transcriptLogRef.current;
    if (log.length === 0) return;
    const pad = (n: number) => String(n).padStart(2, "0");
    const clock = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    const first = new Date(log[0].at);
    const lines = [
      `Session: ${session?.name ?? ""} (${session?.sessionCode ?? sessionId})`,
      `Source language: ${session?.sourceLanguageCode ?? ""}`,
      `Chunks sent for translation, oldest first: ${log.length}`,
      "",
      ...log.map((entry, i) => `${String(i + 1).padStart(3, "0")}  ${clock(new Date(entry.at))}  ${entry.text}`),
    ];
    // The BOM makes Windows apps (Notepad, Word) read Arabic/French correctly.
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `transcript-${session?.sessionCode ?? "session"}-${first.getFullYear()}${pad(first.getMonth() + 1)}${pad(first.getDate())}-${pad(first.getHours())}${pad(first.getMinutes())}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  /**
   * Starts listening. Azure Speech is preferred: it knows the AFA vocabulary
   * (phrase list), handles accents and long-form speech better, and keeps
   * sentences whole. If the server has no Azure Speech configured, or Azure
   * fails, the browser's own recognizer takes over so a live session is never
   * left without any recognizer.
   */
  async function startMic() {
    if (micLiveRef.current) return;
    setMicError(null);
    micLiveRef.current = true;
    setMicLive(true);

    // Both recognizers feed this chunker instead of sending only finished
    // sentences. It commits short chunks (and the live provisional line) while
    // the speaker is still talking, so translation starts after a few words
    // rather than after the whole paragraph.
    const chunker = new UtteranceChunker({
      onCommit: sendUtterance,
      onPartial: sendPartial,
      ...SPEED_PRESETS[liveSettingsRef.current.speed].chunker,
    });
    chunkerRef.current = chunker;
    // Best effort and off the critical path: without the list the chunker simply works as before.
    void apiRequest<{ phrases: string[] }>("/speech/glossary-phrases", { query: { sessionId } })
      .then((res) => {
        if (chunkerRef.current === chunker) chunker.setProtectedPhrases(res.phrases);
      })
      .catch(() => undefined);
    if (chunkPollRef.current) clearInterval(chunkPollRef.current);
    chunkPollRef.current = setInterval(() => chunker.poll(), 100);

    try {
      const handle = await startAzureRecognition(sessionId, {
        onInterim: (text) => {
          if (!micLiveRef.current) return;
          setInterimText(text);
          chunker.feedInterim(text);
        },
        onFinal: (text) => {
          setMicError(null);
          chunker.feedFinal(text);
        },
        onFatalError: (message) => {
          // Azure stopped by itself: keep the session going on the browser recognizer.
          azureRef.current = null;
          setEngine(null);
          chunker.flush();
          if (micLiveRef.current) {
            setMicError(`${message} Switched to the browser's built-in recognizer (less accurate).`);
            startBrowserRecognition();
          }
        },
      }, { pauseMs: liveSettingsRef.current.pauseMs });
      if (!micLiveRef.current) {
        // The operator pressed Stop while Azure was still connecting.
        await handle?.stop();
        return;
      }
      if (handle) {
        azureRef.current = handle;
        setEngine("azure");
        return;
      }
    } catch (err) {
      if (!micLiveRef.current) return;
      const detail = err instanceof Error ? err.message : String(err);
      setMicError(`Azure speech could not start (${detail}). Using the browser's built-in recognizer instead (less accurate).`);
    }
    startBrowserRecognition();
  }

  function startBrowserRecognition() {
    const Recognition = getSpeechRecognition();
    if (!Recognition) {
      micLiveRef.current = false;
      setMicLive(false);
      setMicError("Speech recognition is not supported in this browser. Use Chrome or Edge on desktop.");
      return;
    }
    setEngine("browser");
    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = bcp47(session?.sourceLanguageCode ?? "en");

    recognition.onresult = (event: any) => {
      // A result means the browser's recognition service is reachable and
      // working again — clear any stale error (e.g. a transient "network"
      // blip) instead of leaving the operator staring at an error banner
      // for a problem that already resolved itself.
      setMicError(null);
      const chunker = chunkerRef.current;
      let liveInterim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          chunker?.feedFinal(result[0].transcript.trim());
        } else {
          liveInterim += result[0].transcript;
        }
      }
      // The phrase still being spoken: shown to the operator AND fed to the
      // chunker, which translates it in pieces without waiting for the pause.
      setInterimText(liveInterim.trim());
      if (liveInterim.trim()) chunker?.feedInterim(liveInterim.trim());
    };
    recognition.onerror = (event: any) => {
      if (event.error === "no-speech" || event.error === "aborted") return;
      if (["not-allowed", "service-not-allowed", "audio-capture", "language-not-supported"].includes(event.error)) {
        // Retrying can't fix these (permission denied, no microphone, ...) and
        // onend would otherwise restart recognition in an endless failing loop.
        micLiveRef.current = false;
        setMicLive(false);
        setInterimText("");
        setMicError(
          event.error === "not-allowed" || event.error === "service-not-allowed"
            ? "Microphone access was blocked. Allow the microphone for this site in the browser's address bar, then press Start mic again."
            : `Microphone error: ${event.error}`,
        );
        return;
      }
      if (event.error === "network") {
        // Chrome/Edge send the microphone audio to their own speech
        // recognition service (not Azure) to turn it into text; "network"
        // means that request failed or timed out. onend (below) already
        // restarts recognition automatically, so this is usually transient
        // — say so rather than implying the session is broken.
        setMicError("Microphone: brief connection hiccup with the browser's speech service — retrying automatically…");
        return;
      }
      setMicError(`Microphone error: ${event.error}`);
    };
    recognition.onend = () => {
      // This recognition run is over: deliver any words that never got a final,
      // so they aren't lost or mixed into the next run.
      chunkerRef.current?.flush();
      // Browsers stop recognition after a period of silence; restart
      // automatically while the operator still intends to be live.
      if (micLiveRef.current) {
        try {
          recognition.start();
        } catch {
          /* already starting */
        }
      }
    };

    recognitionRef.current = recognition;
    micLiveRef.current = true;
    setMicLive(true);
    recognition.start();
  }

  function stopMic() {
    micLiveRef.current = false;
    setMicLive(false);
    setInterimText("");
    setEngine(null);
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    void azureRef.current?.stop().catch(() => undefined);
    azureRef.current = null;
    // Stopping makes the recognizer deliver a last final result a moment later;
    // let the chunker handle it, then clear the listeners' live line.
    const chunker = chunkerRef.current;
    chunkerRef.current = null;
    if (chunker) {
      setTimeout(() => {
        chunker.reset();
        // Only stop ticking if a newer microphone run hasn't taken over the timer meanwhile.
        if (chunkerRef.current === null && chunkPollRef.current) {
          clearInterval(chunkPollRef.current);
          chunkPollRef.current = null;
        }
      }, 1500);
    }
  }

  function bcp47(code: string) {
    return code === "ar" ? "ar-SA" : code === "fr" ? "fr-FR" : "en-US";
  }

  if (loadError) {
    return (
      <div>
        <div className="error-banner" style={{ marginBottom: 14 }}>{loadError}</div>
        <Link className="btn-ghost" to="/operator/conferences">
          ← All conferences
        </Link>
      </div>
    );
  }
  if (!session || !qr) return <p>Loading…</p>;

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <Link className="btn-ghost" to={`/operator/conferences/${session.conferenceId}`}>
          ← Sessions
        </Link>
      </div>
      <h1>{session.name}</h1>

      {!connected && (
        <div className="error-banner" style={{ marginBottom: 14 }}>
          Connection to the server lost — reconnecting. Sentences spoken now are not being translated.
        </div>
      )}
      {actionError && <div className="error-banner" style={{ marginBottom: 14 }}>{actionError}</div>}

      <div className="control-panel">
        <div className="stack">
          <div className="card">
            <div className="toolbar" style={{ marginBottom: 6 }}>
              <strong>Session control</strong>
              <span
                className={`status-pill ${status === "LIVE" ? "ok" : status === "PAUSED" ? "warn" : status === "ENDED" ? "bad" : "warn"}`}
              >
                <span className="status-dot" />
                {status}
              </span>
            </div>
            {loadedDictionaries.length > 0 && (
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 8 }}>
                Dictionaries in use, highest priority first:{" "}
                {loadedDictionaries.map((d) => `${d.name} (${d.terms})`).join(" → ")}
              </div>
            )}
            <div className="toolbar">
              {status === "SCHEDULED" && (
                <button className="btn-primary" onClick={() => callControl("start")}>
                  ▶ Start session
                </button>
              )}
              {status === "LIVE" && (
                <button className="btn-secondary" onClick={() => callControl("pause")}>
                  ⏸ Pause
                </button>
              )}
              {status === "PAUSED" && (
                <button className="btn-primary" onClick={() => callControl("resume")}>
                  ▶ Resume
                </button>
              )}
              {status !== "ENDED" && (
                <button className="btn-danger" onClick={() => callControl("stop")}>
                  ⏹ Stop
                </button>
              )}
            </div>
          </div>

          <div className="card">
            <strong>Speaker microphone</strong>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
              Uses this device's microphone (plug it into the room's audio mixer/interface output, or place near the
              speaker). Speech is recognized with Azure Speech, tuned with the AFA terminology dictionary, or with the
              browser's built-in recognizer if Azure isn't available. Recognized sentences are sent to the translation
              pipeline as soon as the session is live.
            </p>
            <div className="mic-indicator" style={{ marginTop: 10 }}>
              <span className={`mic-dot${micLive ? " live" : ""}`} />
              <span>
                {micLive
                  ? `Microphone live — listening${
                      engine === "azure" ? " (Azure Speech)" : engine === "browser" ? " (browser recognizer)" : "…"
                    }`
                  : "Microphone off"}
              </span>
              <div style={{ flex: 1 }} />
              {!micLive ? (
                <button className="btn-primary" disabled={status !== "LIVE"} onClick={startMic}>
                  Start mic
                </button>
              ) : (
                <button className="btn-danger" onClick={stopMic}>
                  Stop mic
                </button>
              )}
            </div>
            {micLive && status !== "LIVE" && (
              <div style={{ marginTop: 10, fontSize: 13, color: "var(--text-muted)" }}>
                Session is {status?.toLowerCase()} — speech is not being translated.
              </div>
            )}
            {micLive && (
              <div
                style={{
                  marginTop: 10,
                  minHeight: 20,
                  fontStyle: "italic",
                  color: "var(--text-muted)",
                  fontSize: 14,
                }}
              >
                {interimText ? `"${interimText}"` : "Listening for speech…"}
              </div>
            )}
            {micError && <div className="error-banner" style={{ marginTop: 10 }}>{micError}</div>}
          </div>

          <LiveSettingsCard
            settings={liveSettings}
            onChange={updateLiveSettings}
            serverAllowsLivePreview={serverLivePartials}
            micLive={micLive}
            delay={delay}
          />

          <div className="card">
            <div className="toolbar" style={{ marginBottom: 0 }}>
              <strong style={{ flex: 1 }}>Live transcript (source)</strong>
              <button className="btn-secondary" disabled={transcript.length === 0} onClick={downloadTranscript}>
                Download transcript
              </button>
            </div>
            <div className="transcript-log" style={{ marginTop: 10 }}>
              {transcript.length === 0 && <span style={{ color: "var(--text-muted)" }}>Nothing spoken yet.</span>}
              {[...transcript].reverse().map((line) => (
                <div className="line" key={line.id}>
                  <span className="lang">{line.lang.toUpperCase()}</span>
                  {line.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="stack">
          <div className="card qr-box">
            <strong>Attendee join</strong>
            <img src={qr.qrDataUrl} alt="QR code to join session" />
            <div className="session-code-big">{qr.sessionCode}</div>
            <div style={{ fontSize: 12.5, color: "var(--text-muted)", wordBreak: "break-all" }}>{qr.joinUrl}</div>
          </div>

          <div className="card">
            <strong>Listeners by language</strong>
            <div className="stack" style={{ marginTop: 10 }}>
              {session.targetLanguageCodes.map((code) => (
                <div key={code} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{code.toUpperCase()}</span>
                  <strong>{listenerCounts[code] ?? 0}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
